import { differenceInCalendarDays } from 'date-fns';
import { GastoPlan, Plan } from '../types';
import { formatoEuros } from './amediasUtils';

/**
 * Recordatorios de deudas en A medias.
 *
 * No hay servidor que mande correos: el recordatorio es un aviso dentro de la
 * app para quien debe, y un mensaje listo para WhatsApp para quien cobra.
 * El plazo se cuenta desde que el plan acabó (fecha de fin) o, si no tiene,
 * desde el último gasto: es cuando ya toca ir saldando.
 */

/** Opciones del plazo en días; 0 = no recordar. */
export const PLAZOS_RECORDATORIO = [0, 3, 7, 15, 30];
export const PLAZO_POR_DEFECTO = 7;

/** Desde cuándo se cuenta el plazo. undefined si aún no hay nada que contar. */
export function referenciaRecordatorio(plan: Pick<Plan, 'fechaFin'>, gastos: GastoPlan[]): Date | undefined {
  if (plan.fechaFin) return plan.fechaFin;
  if (gastos.length === 0) return undefined;
  return gastos.reduce((max, g) => (g.fecha > max ? g.fecha : max), gastos[0].fecha);
}

export interface EstadoRecordatorio {
  /** Ya ha pasado el plazo: toca avisar. */
  vencido: boolean;
  /** Días transcurridos desde la referencia (0 si no la hay o es futura). */
  dias: number;
}

export function estadoRecordatorio(
  plan: Pick<Plan, 'fechaFin' | 'recordatorioDias'>, gastos: GastoPlan[], hoy = new Date(),
): EstadoRecordatorio {
  const ref = referenciaRecordatorio(plan, gastos);
  const dias = ref ? Math.max(0, differenceInCalendarDays(hoy, ref)) : 0;
  const plazo = plan.recordatorioDias ?? 0;
  return { vencido: !!ref && plazo > 0 && dias >= plazo, dias };
}

/** Mensaje para mandar a quien debe. `acreedor` undefined = te debe a ti. */
export function textoRecordatorio(planNombre: string, deudor: string, importe: number, acreedor?: string) {
  const a = acreedor ? `le pases a ${acreedor}` : 'me pases';
  return `Hola ${deudor} 👋 Del plan «${planNombre}» queda pendiente que ${a} ${formatoEuros(importe)}. ¡Gracias!`;
}

/** Enlace de WhatsApp con el texto ya escrito; el destinatario se elige en WhatsApp. */
export const enlaceWhatsApp = (texto: string) => `https://wa.me/?text=${encodeURIComponent(texto)}`;

// ── «Recordármelo más tarde», por dispositivo ────────────────────────────────
// Es una comodidad de quien mira, no un dato del plan: vale localStorage.

const clavePospuesto = (planId: string) => `rzcentral.amedias.pospuesto.${planId}`;

export function pospuestoHasta(planId: string): number {
  try { return Number(localStorage.getItem(clavePospuesto(planId))) || 0; } catch { return 0; }
}

export function posponerRecordatorio(planId: string, dias: number) {
  try {
    localStorage.setItem(clavePospuesto(planId), String(Date.now() + Math.max(1, dias) * 86_400_000));
  } catch { /* sin almacenamiento: el aviso volverá a salir, sin más */ }
}
